import { useState, useEffect } from "react";
import api, { getApiErrorMessage } from "../../lib/api";
import { useAuth } from "../../contexts/AuthContext";
import SaleDetailsDialog from "../../components/SaleDetailsDialog";
import SaleStatusBadge from "../../components/SaleStatusBadge";
import { Button } from "../../components/ui/button";
import {
  POINT_TRANSACTION_KIND,
  formatPoints,
  getPointTransactionKind,
  isSaleReversalTransaction,
} from "../../lib/display";
import { Coins, ArrowUpRight, ArrowDownRight, Loader2, Eye } from "lucide-react";
import { toast } from "sonner";

const getSaleItems = (venda) => {
  if (venda?.itens?.length) return venda.itens;
  if (venda?.produtos?.length) return venda.produtos;
  return [];
};

const getItemName = (item) => (
  item?.produto ||
  item?.descricao_produto ||
  item?.descricao ||
  item?.sku ||
  item?.codigo_produto_erp ||
  "Produto"
);

const getItemsSummary = (items) => {
  if (!items.length) return "Produtos não informados";

  const firstItem = items[0];
  const quantidade = firstItem.quantidade_item ?? firstItem.quantidade;
  const quantityText = quantidade !== undefined && quantidade !== null ? `Qtd: ${quantidade}` : "Qtd. não informada";
  const extraItems = items.length > 1 ? ` +${items.length - 1} ${items.length === 2 ? "item" : "itens"}` : "";

  return `${getItemName(firstItem)} (${quantityText})${extraItems}`;
};

const stripCurrencyFromText = (value = "") => (
  value
    .replace(/\s*-\s*R\$\s*[\d.,]+/gi, "")
    .replace(/\bR\$\s*[\d.,]+/gi, "")
    .replace(/\s{2,}/g, " ")
    .trim()
);

const getSaleCode = (venda) => (
  venda?.numero_documento ||
  venda?.id_venda_erp ||
  venda?.id?.slice(0, 8) ||
  "Venda"
);

const getTransactionTitle = (transacao, venda) => {
  const cleanDescription = stripCurrencyFromText(transacao?.descricao || "");
  const isSaleReversal = getPointTransactionKind(transacao) === POINT_TRANSACTION_KIND.SALE_REVERSAL;

  if (isSaleReversal) return cleanDescription || "Estorno de pontos ERP";
  if (!venda) return cleanDescription || "Movimentação de pontos";

  const originLabel = venda.origem === "erp" ? "Venda ERP" : venda.origem === "qr" ? "Venda QR" : "Venda";
  return `${originLabel} #${getSaleCode(venda)}`;
};

const filterOptions = [
  { value: "all", label: "Todos", empty: "Nenhuma transação ainda" },
  { value: POINT_TRANSACTION_KIND.EARNED, label: "Ganhos", empty: "Nenhum ganho registrado" },
  { value: POINT_TRANSACTION_KIND.SPENT, label: "Gastos", empty: "Nenhum gasto registrado" },
];

const transactionStyles = {
  [POINT_TRANSACTION_KIND.EARNED]: {
    bg: "bg-green-100",
    icon: "text-green-600",
    points: "text-green-600",
    Icon: Coins,
  },
  [POINT_TRANSACTION_KIND.SPENT]: {
    bg: "bg-red-100",
    icon: "text-red-600",
    points: "text-red-600",
    Icon: Coins,
  },
};

const formatSignedPoints = (value) => {
  const numberValue = Number(value || 0);
  return `${numberValue >= 0 ? "+" : "-"}${formatPoints(Math.abs(numberValue))}`;
};

const PartnerHistorico = () => {
  const { user } = useAuth();
  const [transacoes, setTransacoes] = useState([]);
  const [vendasMap, setVendasMap] = useState({});
  const [selectedVenda, setSelectedVenda] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    fetchTransacoes();
  }, [user]);

  const fetchTransacoes = async () => {
    try {
      if (user?.parceiro_id) {
        const [transacoesRes, vendasRes] = await Promise.all([
          api.get(`/transacoes?parceiro_id=${user.parceiro_id}`),
          api.get("/vendas"),
        ]);

        const vendasById = {};
        vendasRes.data.forEach((venda) => {
          vendasById[venda.id] = venda;
        });

        setTransacoes(transacoesRes.data);
        setVendasMap(vendasById);
      }
    } catch (error) {
      console.error("Error fetching transactions:", error);
      toast.error(getApiErrorMessage(error, "Erro ao carregar histórico de pontos."));
    } finally {
      setLoading(false);
    }
  };

  const transacoesVisiveis = transacoes.filter(
    (transacao) => !isSaleReversalTransaction(transacao),
  );

  const filteredTransacoes = transacoesVisiveis.filter((t) => {
    if (filter === "all") return true;
    return getPointTransactionKind(t) === filter;
  });

  const totalGanho = transacoes.reduce((sum, t) => {
    const kind = getPointTransactionKind(t);
    if (kind === POINT_TRANSACTION_KIND.EARNED) return sum + Number(t.pontos || 0);
    if (kind === POINT_TRANSACTION_KIND.SALE_REVERSAL) return sum - Number(t.pontos || 0);
    return sum;
  }, 0);
  const totalGasto = transacoes
    .filter(t => getPointTransactionKind(t) === POINT_TRANSACTION_KIND.SPENT)
    .reduce((sum, t) => sum + Number(t.pontos || 0), 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-w-0 space-y-6 animate-fade-in" data-testid="partner-historico">
      <h1 className="font-heading text-2xl font-bold text-slate-900">
        Histórico de Pontos
      </h1>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div className="bg-green-50 rounded-2xl p-4">
          <div className="flex items-center gap-2 text-green-600 mb-2">
            <ArrowUpRight className="h-5 w-5" />
            <span className="text-sm font-medium">Ganhos líquidos</span>
          </div>
          <p className="font-heading text-2xl font-bold text-green-700">
            {formatSignedPoints(totalGanho)}
          </p>
        </div>
        <div className="bg-red-50 rounded-2xl p-4">
          <div className="flex items-center gap-2 text-red-600 mb-2">
            <ArrowDownRight className="h-5 w-5" />
            <span className="text-sm font-medium">Gastos</span>
          </div>
          <p className="font-heading text-2xl font-bold text-red-700">
            -{formatPoints(totalGasto)}
          </p>
        </div>
      </div>

      {/* Filter */}
      <div className="flex flex-wrap gap-2">
        {filterOptions.map((option) => (
          <button
            key={option.value}
            onClick={() => setFilter(option.value)}
            className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
              filter === option.value
                ? "bg-primary text-white"
                : "bg-slate-100 text-slate-600 hover:bg-slate-200"
            }`}
            data-testid={`filter-${option.value}`}
          >
            {option.label}
          </button>
        ))}
      </div>

      {/* Transactions List */}
      <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
        <div className="divide-y divide-slate-100">
          {filteredTransacoes.length > 0 ? (
            filteredTransacoes.map((transacao) => {
              const venda = transacao.venda_id ? vendasMap[transacao.venda_id] : null;
              const items = getSaleItems(venda);
              const clienteNome = venda?.cliente_erp?.nome_cliente;
              const transactionKind = getPointTransactionKind(transacao);
              const style = transactionStyles[transactionKind] || transactionStyles[POINT_TRANSACTION_KIND.EARNED];
              const TransactionIcon = style.Icon;
              const pointsValue = Number(transacao.pontos || 0);
              const signedPoints = transactionKind === POINT_TRANSACTION_KIND.EARNED ? pointsValue : -pointsValue;

              return (
                <div key={transacao.id} className="flex flex-col gap-3 p-4 sm:flex-row sm:items-start sm:justify-between">
                  <div className="flex min-w-0 items-start gap-3">
                    <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full ${style.bg}`}>
                      <TransactionIcon className={`h-5 w-5 ${style.icon}`} />
                    </div>
                    <div className="min-w-0">
                      <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                        <p className="break-words text-sm font-medium text-slate-900">
                          {getTransactionTitle(transacao, venda)}
                        </p>
                        {venda && <SaleStatusBadge status={venda.status_venda} />}
                      </div>
                      {venda && (
                        <div className="mt-1 flex flex-wrap gap-x-3 gap-y-1 text-xs text-slate-500">
                          <span>{clienteNome || "Cliente não informado"}</span>
                          <span className="break-words">{getItemsSummary(items)}</span>
                        </div>
                      )}
                      <p className="mt-1 text-xs text-slate-500">
                        {new Date(transacao.created_at).toLocaleDateString('pt-BR', {
                          day: '2-digit',
                          month: 'short',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </p>
                      {venda && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setSelectedVenda(venda)}
                          className="mt-3 h-8 px-3 text-xs"
                          data-testid={`partner-sale-details-${venda.id}`}
                        >
                          <Eye className="mr-1 h-3.5 w-3.5" />
                          Ver detalhes
                        </Button>
                      )}
                    </div>
                  </div>
                  <span className={`shrink-0 self-end font-heading text-lg font-bold sm:self-auto ${style.points}`}>
                    {formatSignedPoints(signedPoints)}
                  </span>
                </div>
              );
            })
          ) : (
            <div className="p-12 text-center text-slate-500">
              {filterOptions.find((option) => option.value === filter)?.empty || "Nenhuma transação ainda"}
            </div>
          )}
        </div>
      </div>

      <SaleDetailsDialog
        open={Boolean(selectedVenda)}
        onOpenChange={(open) => !open && setSelectedVenda(null)}
        venda={selectedVenda}
        showSaleValues={false}
      />
    </div>
  );
};

export default PartnerHistorico;
